import { ArrowDownRight, ArrowUpRight } from "lucide-react"
import type { ReactNode } from "react"

import { cn } from "../../lib/utils"

type MetricTrendProps = {
  current: number
  previous: number
  /** Tooltip context, e.g. "vs previous 7 days". */
  title: string
}

/** Period-over-period change for a card metric: the signed percent vs the
 *  previous window of equal length. Up is good (green), down is bad (red).
 *  When the previous window was empty there is no ratio to form — a non-zero
 *  current reads as "new"; a still-empty current renders nothing. */
export function MetricTrend({ current, previous, title }: MetricTrendProps) {
  if (previous === 0) {
    if (current === 0) {
      return null
    }

    return (
      <TrendChip tone="up" title={title} icon={<ArrowUpRight className="size-3" />} label="new" />
    )
  }

  const pct = Math.round(((current - previous) / previous) * 100)
  if (pct === 0) {
    return (
      <span className="text-xs tabular-nums text-muted-foreground/60" title={title}>
        0%
      </span>
    )
  }

  const up = pct > 0
  return (
    <TrendChip
      tone={up ? "up" : "down"}
      title={title}
      icon={up ? <ArrowUpRight className="size-3" /> : <ArrowDownRight className="size-3" />}
      label={`${Math.abs(pct)}%`}
    />
  )
}

function TrendChip({
  tone,
  icon,
  label,
  title,
}: {
  tone: "up" | "down"
  icon: ReactNode
  label: string
  title: string
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-xs font-medium tabular-nums",
        tone === "up" ? "text-success" : "text-destructive"
      )}
      title={title}
    >
      {icon}

      {label}
    </span>
  )
}
